/**
 * Meeting formatting utilities for Course Finder
 *
 * Specialized utilities for formatting and sorting meeting-related data
 * (meeting times, meeting days, meeting patterns, etc.)
 */

/**
 * Chronological sorting for meeting times
 *
 * Sorts meeting times in chronological order (not alphabetical)
 * Handles various time formats including ranges and single times
 * For same start times, sorts by end time (shorter duration first)
 */
export const sortMeetingTimes = (items) => {
  const parseTime = (timeStr) => {
    // Handle formats like "8 a.m.-10 a.m.", "8-10 a.m.", "2:30 p.m.", etc.
    const match = timeStr.match(/(\d{1,2})(?::(\d{2}))?\s*(a\.m\.|p\.m\.|AM|PM)/i);
    if (!match) return 24 * 60; // Put unparseable times at end

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2] || '0', 10);
    const period = match[3].toLowerCase();

    // Convert to 24-hour format
    if (period.includes('p') && hours !== 12) {
      hours += 12;
    } else if (period.includes('a') && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes; // Return minutes from midnight
  };

  const parseTimeRange = (timeStr) => {
    // Find all time patterns in the string
    const timePattern = /(\d{1,2})(?::(\d{2}))?\s*(a\.m\.|p\.m\.|AM|PM)/gi;
    const matches = [...timeStr.matchAll(timePattern)];

    if (matches.length === 0) {
      return { start: 24 * 60, end: 24 * 60 }; // Put unparseable times at end
    }

    // Parse start time (first match)
    const start = parseTime(matches[0][0]);

    // Parse end time (second match, or same as start if only one time)
    const end = matches.length > 1 ? parseTime(matches[1][0]) : start;

    return { start, end };
  };

  return [...items].sort((a, b) => {
    const timeRangeA = parseTimeRange(a.label);
    const timeRangeB = parseTimeRange(b.label);

    // Primary sort: by start time
    if (timeRangeA.start !== timeRangeB.start) {
      return timeRangeA.start - timeRangeB.start;
    }

    // Secondary sort: by end time (shorter duration first)
    return timeRangeA.end - timeRangeB.end;
  });
};

/**
 * Meeting days formatter following Illinois State editorial guidelines
 *
 * Formats meeting days based on the number of days:
 * - Single days: Full words (M = Monday)
 * - 2+ days: Three-character abbreviations (M W = Mon / Wed, M W F = Mon / Wed / Fri)
 */
export const formatMeetingDays = (daysString) => {
  if (!daysString) return daysString;

  // Day mappings
  const dayMappings = {
    'M': { full: 'Monday', abbrev: 'Mon' },
    'Tu': { full: 'Tuesday', abbrev: 'Tue' },
    'W': { full: 'Wednesday', abbrev: 'Wed' },
    'Th': { full: 'Thursday', abbrev: 'Thu' },
    'F': { full: 'Friday', abbrev: 'Fri' },
    'Sa': { full: 'Saturday', abbrev: 'Sat' },
    'Su': { full: 'Sunday', abbrev: 'Sun' }
  };

  // Split by spaces and filter out empty strings
  const days = daysString.split(/\s+/).filter(day => day.length > 0);

  if (days.length === 0) return daysString;

  // Single day: use full word
  if (days.length === 1) {
    const day = days[0];
    return dayMappings[day]?.full || day;
  }

  // 2+ days: use three-character abbreviations with " / " separator
  return days
    .map(day => dayMappings[day]?.abbrev || day)
    .join(' / ');
};

/**
 * Chronological sorting for meeting days
 *
 * Sorts meeting days in chronological order (Monday through Sunday)
 */
export const sortMeetingDays = (items) => {
  const dayOrder = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];

  const getDayWeight = (daysString) => {
    if (!daysString) return 999; // Put invalid entries at end

    // Split by spaces and get the first day for sorting
    const days = daysString.split(/\s+/).filter(day => day.length > 0);
    if (days.length === 0) return 999;

    const firstDay = days[0];
    const index = dayOrder.indexOf(firstDay);
    return index === -1 ? 999 : index;
  };

  return [...items].sort((a, b) => {
    const weightA = getDayWeight(a.label);
    const weightB = getDayWeight(b.label);

    if (weightA !== weightB) {
      return weightA - weightB;
    }

    // If same starting day, sort by number of days (fewer first)
    const daysA = a.label.split(/\s+/).filter(day => day.length > 0).length;
    const daysB = b.label.split(/\s+/).filter(day => day.length > 0).length;

    return daysA - daysB;
  });
};

// Grouped exports for organized importing
export const meetingFormatting = {
  sortMeetingTimes,
  formatMeetingDays,
  sortMeetingDays
};